import React from 'react'
import Chip from '@mui/material/Chip'
import { hashHistory } from 'react-router'
import TableInc from '../../../../shared-ui/TableInc'
import ButtonInc from '../../../../shared-ui/ButtonInc'
import { useProductCurrencies } from '../../../../hooks/useProductCurrency'
import { formatDateLocale } from '../../../../lib/utils.es6'
import {
  principalDependant,
  policyStatusLabel
} from '../../../../lib/policyDetails.es6'
import { stageLabel } from '../../../../components/RecentPolicies/Components/makerCheckStage'
import { NEW_BUSINESS, hasRequirementPending } from '../lib/agentPolicies'

function principalName(policy) {
  const dependant = principalDependant(policy)
  if (!dependant) {
    return 'N/A'
  }
  const name = [
    dependant.first_name,
    dependant.middle_name,
    dependant.last_name
  ]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name || dependant.full_name || 'N/A'
}

const AgentPoliciesTable = (props) => {
  const policies = props.policies || []

  // Premiums show the product's currency, not the partner country's — OMK
  // products are priced in KES and the two can differ.
  const resolveCurrency = useProductCurrencies(
    policies.map(function(policy) {
      return policy && policy.product_code
    })
  )

  const rows = policies.map(function(policy) {
    const quotation = policy.quotation || {}
    const cents = quotation.total_premium_amount_in_cents

    return {
      ...policy,
      principal: principalName(policy),
      premium:
        typeof cents === 'number'
          ? `${resolveCurrency(policy.product_code)} ${cents / 100}`
          : 'N/A',
      status_label: policyStatusLabel(policy).label,
      qa_status: stageLabel(policy),
      requirement_pending: hasRequirementPending(policy),
      created_at_label: formatDateLocale(policy.created_at) || 'N/A',
      start_date_label: formatDateLocale(policy.start_date) || 'N/A',
      renewal_date_label: formatDateLocale(policy.due_date) || 'N/A'
    }
  })

  const sharedColumns = [
    { title: 'Policy Number', field: 'policy_number' },
    { title: 'Principal', field: 'principal' },
    { title: 'Premium', field: 'premium' },
    { title: 'Policy Status', field: 'status_label' }
  ]

  const newBusinessColumns = [
    { title: 'QA Status', field: 'qa_status' },
    {
      title: 'Requirement Pending',
      field: 'requirement_pending',
      render: function(row) {
        return row.requirement_pending ? (
          <Chip label="Action required" color="warning" size="small" />
        ) : (
          <span>None</span>
        )
      }
    },
    { title: 'Date Created', field: 'created_at_label' }
  ]

  const portfolioColumns = [
    { title: 'Start Date', field: 'start_date_label' },
    // The engine's next-premium due date. On an annual Last Expense policy
    // that IS the renewal date.
    { title: 'Renewal Date', field: 'renewal_date_label' }
  ]

  const actionColumn = {
    title: 'Actions',
    field: 'actions',
    render: function(row) {
      return (
        <ButtonInc
          variant="contained"
          sx={{ textTransform: 'none' }}
          onClick={function() {
            hashHistory.push(`admin/policy-processing/${row.policy_number}`)
          }}
        >
          View Policy
        </ButtonInc>
      )
    }
  }

  const columns = sharedColumns
    .concat(props.view === NEW_BUSINESS ? newBusinessColumns : portfolioColumns)
    .concat([actionColumn])

  return (
    <TableInc
      columns={columns}
      data={rows}
      isLoading={props.loading}
      localization={{
        body: {
          emptyDataSourceMessage:
            props.view === NEW_BUSINESS
              ? 'No business submitted and awaiting acceptance.'
              : 'No accepted policies yet.'
        }
      }}
      options={{
        pageSize: 20,
        paging: true,
        showTitle: false,
        // The whole filtered set is held here, so material-table counts and
        // paginates it locally and its search box works over all of it.
        search: true,
        emptyRowsWhenPaging: false
      }}
    />
  )
}

export default AgentPoliciesTable
